import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, split } from '@apollo/client/core'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { context, trace } from '@opentelemetry/api'
import * as Sentry from '@sentry/nextjs'
import { TokenRefreshLink } from 'apollo-link-token-refresh'
import 'isomorphic-fetch'
import { getCookie } from 'cookies-next'
import debug from 'debug'
import { createClient } from 'graphql-ws'
import { OperationTypeNode } from 'graphql/language/ast'
import { Kind } from 'graphql/language/kinds'
import WebSocket from 'isomorphic-ws'
import { getEnv, getPublicEnv } from '@tucan/utils'
import { getNumber } from '../utils/get.number'
import { isBrowser } from '../utils/is.browser'

const logger = debug('gql')

let client: ApolloClient<any>

export const hasGraphQLError = (e: any, message): boolean => {
  if (!e.graphQLErrors) {
    return false
  }

  for (const error of e.graphQLErrors) {
    if (error.message === message) {
      return true
    }
  }

  return false
}

const getClient = (): ApolloClient<any> => {
  if (!client) {
    const getUri = (options: { path?: string; websocket?: boolean } = {}): string => {
      const url = new URL(
        String(getEnv('ENDPOINT') || getPublicEnv('ENDPOINT')),
        isBrowser() ? window.location.href : 'http://localhost',
      )

      const base = url.pathname === '/' ? '' : url.pathname

      if (options.path) {
        url.pathname = `${base}${options.path}`
      } else {
        url.pathname = `${base}/graphql`
      }

      if (options.websocket) {
        url.protocol = url.protocol.replace('http', 'ws')
      }

      return url.toString()
    }

    client = new ApolloClient({
      cache: new InMemoryCache(),
      link: from([
        onError(({ graphQLErrors, networkError, operation }) => {
          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) =>
              logger(
                '[GraphQL error]: Message: %s, Location: %O, Path: %O, Operation: %O',
                message,
                locations,
                path,
                operation,
              ),
            )
          }

          if (networkError) {
            logger('[Network error]: %O', networkError)
          }
        }),
        (new TokenRefreshLink({
          accessTokenField: 'login',
          fetchAccessToken: (): Promise<Response> => {
            return fetch(getUri({ path: '/auth/refresh' }), {
              credentials: 'include',
              method: 'POST',
            })
          },
          handleError: (error) => {
            logger('failed to refresh token %O', error)
            Sentry.captureException(error)
            localStorage.removeItem('f')

            if (error.message === 'refresh token invalid' && localStorage.getItem('refresh')) {
              // TODO logout as refresh token response is no longer valid!
              localStorage.removeItem(`${getPublicEnv('COOKIE_PREFIX')}expires`)
              window.location.reload()
            }

            // TODO trigger this in a nicer way, this can happen for various reasons, network timeouts, etc. Retry!
            //window.location.reload()
          },
          handleFetch: (next: { loggedIn: boolean; expires: number }) => {
            localStorage.removeItem('f')
          },
          handleResponse: () => async (response) => {
            const result = await response.json()

            if (result.error) {
              throw new Error(result.error)
            }

            return result
          },
          isTokenValidOrUndefined: async () => {
            if (!isBrowser() || !getCookie(`${getPublicEnv('COOKIE_PREFIX')}expires`)) {
              return true
            }

            if (localStorage.getItem('f')) {
              return false
            }

            try {
              const expires = Number(getCookie(`${getPublicEnv('COOKIE_PREFIX')}expires`))

              return expires >= Date.now() / 1000 + 30
            } catch (e) {
              return false
            }
          },
        }) as unknown) as ApolloLink,
        setContext(async (request, ctx) => {
          const headers: any = {}

          if (isBrowser()) {
            if (ctx.recaptcha) {
              headers.recaptcha = ctx.recaptcha
            }
            if (!ctx.ignoreCompanyHeader) {
              headers['x-company-id'] = localStorage.getItem('company')
            }

            headers['x-language'] = localStorage.getItem('i18nextLng')
          } else {
            const span = trace.getSpan(context.active())
            if (span) {
              const {spanId, traceId} = span.spanContext()

              headers['X-B3-TraceId'] = traceId
              headers['X-B3-ParentSpanId'] = spanId
            }
          }

          if (ctx.Authorization) {
            headers['Authorization'] = ctx.Authorization
          }

          return {
            headers,
          }
        }),
        split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === Kind.OPERATION_DEFINITION &&
              definition.operation === OperationTypeNode.SUBSCRIPTION
            )
          },
          new GraphQLWsLink(createClient({
            connectionParams: () => {
              if (!isBrowser()) {
                return {}
              }

              return {
                isWebSocket: true,
              }
            },
            url: getUri({ websocket: true }),
            webSocketImpl: WebSocket,
          })),
          split(
            (op) => {
              if (getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')) === 0) {
                return false
              }

              const batch = op.getContext()['batch']

              return batch === undefined || batch === true
            },
            new BatchHttpLink({
              batchInterval: getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')),
              uri: getUri(),
            }),
            new HttpLink({
              uri: getUri(),
            }),
          ),
        ),
      ]),
    })
  }

  return client
}

export default getClient
